import { BlockedPeriod, DayAvailability } from '../models/meeting-preferences';

/** The most availability windows a single day may hold. */
export const MAX_INTERVALS_PER_DAY = 8;

/** The most meetings a counselor can take in a single day. */
export const MAX_MEETINGS_PER_DAY = 10;

/** The most characters a counselor's default meeting instructions may hold.
 *  Mirrors MAX_INSTRUCTIONS_LENGTH in userMeetingPreferences.controller.js. */
export const MAX_INSTRUCTIONS_LENGTH = 500;

/** Minutes since midnight, so interval comparisons ignore the date part. */
export function minutesOf(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Validation message for one day, or null when valid. Checks the interval count
 * is within range, that every interval is complete, that end follows start, and
 * that none overlap.
 */
export function dayError(day: DayAvailability): string | null {
  if (!day.enabled) return null;

  if (day.intervals.length > MAX_INTERVALS_PER_DAY) {
    return `A day can have at most ${MAX_INTERVALS_PER_DAY} intervals.`;
  }

  for (const interval of day.intervals) {
    if (!interval.start || !interval.end) {
      return 'Set a start and end time for each interval.';
    }
    if (minutesOf(interval.start) >= minutesOf(interval.end)) {
      return 'End time must be after start time.';
    }
  }

  const sorted = [...day.intervals].sort((a, b) => minutesOf(a.start!) - minutesOf(b.start!));
  for (let i = 1; i < sorted.length; i++) {
    if (minutesOf(sorted[i].start!) < minutesOf(sorted[i - 1].end!)) {
      return "Intervals can't overlap.";
    }
  }

  return null;
}

/** First offending day's message, or null when the whole week is valid. */
export function weeklyHoursError(days: DayAvailability[]): string | null {
  for (const day of days) {
    const error = dayError(day);
    if (error) return `${day.day}: ${error}`;
  }
  return null;
}

export function meetingDurationError(durations: number[] | null[]): string | null {
  if (durations.length === 0) return 'Meetings must have at least one allowed duration';
  return null;
}

export function meetingsPerDayError(value: number | null): string | null {
  if (value === null) return null;
  if (!Number.isInteger(value) || value < 1 || value > MAX_MEETINGS_PER_DAY) {
    return `Meetings per day must be between 1 and ${MAX_MEETINGS_PER_DAY}.`;
  }
  return null;
}

/**
 * Validation message for the default meeting instructions, or null when valid.
 * The textarea's own maxlength normally prevents this, so it mainly guards a
 * value that arrived from elsewhere.
 */
export function instructionsError(value: string | null): string | null {
  if (value === null) return null;
  if (value.length > MAX_INSTRUCTIONS_LENGTH) {
    return `Instructions must be ${MAX_INSTRUCTIONS_LENGTH} characters or fewer.`;
  }
  return null;
}

/** Whether `date`'s calendar day falls within a block's [startDate, endDate] range. */
function blockCoversDate(block: BlockedPeriod, date: Date): boolean {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const start = new Date(
    block.startDate.getFullYear(),
    block.startDate.getMonth(),
    block.startDate.getDate(),
  ).getTime();
  const end = new Date(
    block.endDate.getFullYear(),
    block.endDate.getMonth(),
    block.endDate.getDate(),
  ).getTime();
  return day >= start && day <= end;
}

/**
 * Whether `date` is blocked off in its entirety — either a date-range block
 * (which always blocks the full day) or a single-day block explicitly marked
 * all-day.
 */
export function isDateFullyBlocked(blocks: BlockedPeriod[], date: Date): boolean {
  return blocks.some((block) => block.allDay && blockCoversDate(block, date));
}

/**
 * The specific time windows blocked on `date`, in minutes since midnight.
 * Only non-all-day blocks contribute here — a fully-blocked day is handled by
 * `isDateFullyBlocked` instead.
 */
export function blockedWindowsOn(
  blocks: BlockedPeriod[],
  date: Date,
): { startMinutes: number; endMinutes: number }[] {
  return blocks
    .filter((block) => !block.allDay && blockCoversDate(block, date))
    .map((block) => ({
      startMinutes: minutesOf(block.startTime!),
      endMinutes: minutesOf(block.endTime!),
    }));
}

/**
 * Validation message for one blocked period, or null when valid. Mirrors the
 * backend's blockedAvailabilityIntervals checks in validatePreferences.
 */
export function blockedPeriodError(block: BlockedPeriod): string | null {
  if (block.endDate.getTime() < block.startDate.getTime()) {
    return 'End date must be on or after the start date.';
  }

  if (!block.allDay) {
    if (block.startDate.toDateString() !== block.endDate.toDateString()) {
      return 'A time window can only be set for a single day.';
    }
    if (!block.startTime || !block.endTime) {
      return 'Set a start and end time.';
    }
    if (minutesOf(block.startTime) >= minutesOf(block.endTime)) {
      return 'End time must be after start time.';
    }
  }

  return null;
}

/** First offending block's message, or null when every blocked period is valid. */
export function blockedPeriodsError(blocks: BlockedPeriod[]): string | null {
  for (const block of blocks) {
    const error = blockedPeriodError(block);
    if (error) return error;
  }
  return null;
}
